import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ZapIcon, DatabaseIcon, LockIcon, ActivityIcon, BuildingIcon, ShoppingCartIcon, CodeIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteHostingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website hosting | Snelle & veilige hosting | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Betrouwbare website hosting voor WordPress. Snelle servers, SSL-certificaat, dagelijkse backups en 99.9% uptime. Hosting speciaal geoptimaliseerd voor jouw website.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website hosting | Snelle & veilige hosting | Noord',
      'og:description': 'Betrouwbare website hosting voor WordPress. Snelle servers, SSL-certificaat, dagelijkse backups en 99.9% uptime.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/website-hosting',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Is hosting verplicht voor een website?',
    a: 'Ja, elke website heeft hosting nodig om online te zijn. Of je nu een kleine bedrijfswebsite hebt of een uitgebreide webshop, zonder hosting is je website niet bereikbaar voor bezoekers. Wij regelen betrouwbare hosting zodat jij je geen zorgen hoeft te maken over techniek.'
  }, {
    q: 'Zit hosting inbegrepen bij een nieuwe website?',
    a: 'Ja, bij Noord Digital is hosting standaard inbegrepen wanneer je een website bij ons laat maken. Je krijgt snelle servers, dagelijkse backups, SSL-beveiliging en monitoring. Alles in één pakket zonder verborgen kosten.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2164">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2165">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2166">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2167" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2168" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2169" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2170">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2171">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2172">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2173">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2174">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2175">/</span>
            <span className="text-accent-light font-medium" data-id="element-2176">
              Website hosting
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2177">
            Snelle en veilige website hosting
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2178">
            Betrouwbare hosting voor professionele websites. Razendsnel, altijd
            beveiligd en volledig beheerd door ons.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2179">
            <a href="#hosting-voordelen" className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2180">
              Bekijk hosting
              <ArrowRightIcon className="w-5 h-5" data-id="element-2181" />
            </a>
          </div>
        </div>
      </section>

      {/* Hosting voordelen */}
      <section id="hosting-voordelen" className="py-24 bg-surface-muted" data-id="element-2182">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2183">
          <div className="text-center mb-16" data-id="element-2184">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2185">
              Waarom kiezen voor onze website hosting?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2186">
              Onze hosting is geoptimaliseerd voor snelheid, veiligheid en
              betrouwbaarheid.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2187">
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2188">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2189">
                <ZapIcon className="w-6 h-6 text-accent" data-id="element-2190" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2191">
                Snelle servers
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2192">
                Onze servers zijn geoptimaliseerd voor WordPress. Jouw website
                laadt razendsnel, wat zorgt voor een betere gebruikerservaring
                en hogere Google-ranking.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2193">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2194">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-2195" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2196">
                Dagelijkse backups
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2197">
                Elke dag wordt er automatisch een volledige backup gemaakt van
                jouw website en database. Bij problemen herstellen we alles
                snel.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2198">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2199">
                <LockIcon className="w-6 h-6 text-accent" data-id="element-2200" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2201">
                SSL-beveiliging
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2202">
                Gratis SSL-certificaat inbegrepen. Jouw website is beveiligd met
                HTTPS, wat vertrouwen wekt bij bezoekers en door Google wordt
                beloond.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-2203">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2204">
                <ActivityIcon className="w-6 h-6 text-accent" data-id="element-2205" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2206">
                24/7 Monitoring
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2207">
                Wij monitoren jouw website continu op uptime en prestaties. Bij
                problemen grijpen we direct in, vaak voordat jij het merkt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Voor wie */}
      <section className="py-24 bg-white" data-id="element-2208">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2209">
          <div className="text-center mb-16" data-id="element-2210">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2211">
              Voor wie is onze hosting geschikt?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2212">
              Onze snelle website hosting is geschikt voor uiteenlopende
              websites en bedrijven.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2213">
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-2214">
              <div className="w-14 h-14 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-2215">
                <BuildingIcon className="w-7 h-7 text-accent" data-id="element-2216" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2217">
                Bedrijven
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2218">
                Betrouwbare hosting voor bedrijfswebsites die altijd online en
                snel bereikbaar moeten zijn voor klanten.
              </p>
            </div>
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-2219">
              <div className="w-14 h-14 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-2220">
                <ShoppingCartIcon className="w-7 h-7 text-accent" data-id="element-2221" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2222">Webshops</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2223">
                Snelle en veilige hosting voor webshops waar betrouwbaarheid en
                laadsnelheid direct invloed hebben op de omzet.
              </p>
            </div>
            <div className="border border-navy-100 rounded-card p-8 text-center" data-id="element-2224">
              <div className="w-14 h-14 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-2225">
                <CodeIcon className="w-7 h-7 text-accent" data-id="element-2226" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2227">
                WordPress websites
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2228">
                Hosting die speciaal is geoptimaliseerd voor WordPress, met de
                juiste serverinstellingen voor maximale prestaties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-2229">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2230">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2231">
            Gerelateerde pagina's:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2232">
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2233">
              Website onderhoud
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2234">•</span>
            <a href="#/hosting-onderhoud" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2235">
              Website beveiliging
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2236">•</span>
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2237">
              WordPress websites
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-2238">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2239">
          <div className="text-center mb-16" data-id="element-2240">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2241">
              Veelgestelde vragen over website hosting
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2242">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-2243">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2244">
                  <span className="font-bold text-navy-900" data-id="element-2245">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2246" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2247" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2248">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2249" />
    </main>;
}